import { useCallback, useEffect, useRef } from 'react'

import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { useAuthInfo } from '@sweetauth/core'

import { API_URL } from '@/constants/env'

import { SweetspotError } from '@/helpers/error'

import { enabledMcpServersQueryKeys } from '@/hooks/mcp/types'

import {
  authenticateMCPServer,
  requestMicrosoftAdminConsent,
} from '@/lib/oauth'

import type { UUID } from '@/types/Id'
import type { OrganizationMCPServer } from '@/types/MCPServer'

interface UseStoreMCPOAuthTokensArgs {
  orgId: UUID | ''
  microsoftOrgServer?: OrganizationMCPServer | undefined
  orgServer?: OrganizationMCPServer | undefined
  onTokensStored?: () => void
  onStoreStart?: () => void
  onStoreError?: () => void
}

interface StartOAuthFlowOptions {
  forceConsentPrompt?: boolean
  orgServer?: OrganizationMCPServer
}

const persistManagedMicrosoftAdminConsent = async ({
  accessToken,
  microsoftOrgServer,
  orgId,
}: {
  accessToken: string | undefined | null
  microsoftOrgServer: OrganizationMCPServer
  orgId: UUID | ''
}): Promise<void> => {
  if (!accessToken) {
    throw new Error('No access token provided.')
  }

  if (orgId === '') {
    throw new Error('Organization ID is required.')
  }

  const response = await fetch(
    `${API_URL}/api/v1/organizations/${orgId}/mcp-servers/${microsoftOrgServer.mcp_server_id}`,
    {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        oauth_provider_config: {
          ...(microsoftOrgServer.oauth_provider_config || {}),
          admin_consent_granted: true,
        },
      }),
    },
  )

  if (!response.ok) {
    throw new Error('Failed to record Microsoft org consent.')
  }
}

const stringifyResponseDetail = (detail: unknown): string | undefined => {
  if (typeof detail === 'string') {
    return detail
  }

  if (detail == null) {
    return undefined
  }

  try {
    return JSON.stringify(detail)
  } catch {
    return undefined
  }
}

const readMCPTokenStoreError = async (response: Response): Promise<string> => {
  const fallback = `Failed to save authentication tokens (${response.status})`
  const contentType = response.headers.get('content-type') ?? ''

  if (!contentType.includes('application/json')) {
    const text = await response.text().catch(() => '')
    return text ? `${fallback}: ${text}` : fallback
  }

  const body = (await response.json().catch(() => null)) as {
    detail?: unknown
    message?: unknown
  } | null
  const detail =
    stringifyResponseDetail(body?.detail) ??
    stringifyResponseDetail(body?.message)

  return detail ? `${fallback}: ${detail}` : fallback
}

/**
 * Shared hook that:
 * 1. Listens for `mcp_oauth_tokens` postMessage from the OAuth popup
 * 2. POSTs the tokens to the MCP token storage endpoint
 * 3. Invalidates org server queries so `user_has_token` updates
 *
 * Returns `startOAuthFlow` to kick off the OAuth popup via `authenticateMCPServer`.
 */
export const useStoreMCPOAuthTokens = ({
  orgId,
  microsoftOrgServer,
  orgServer,
  onTokensStored,
  onStoreStart,
  onStoreError,
}: UseStoreMCPOAuthTokensArgs) => {
  const activeOrgServer = orgServer ?? microsoftOrgServer
  const { accessToken, user } = useAuthInfo()
  const accessTokenRef = useRef(accessToken)
  accessTokenRef.current = accessToken
  const pendingOAuthServerIdRef = useRef<string | null>(null)
  const pendingOAuthOrgServerRef = useRef<OrganizationMCPServer | null>(null)
  const queryClient = useQueryClient()

  // Listen for mcp_oauth_tokens message from the OAuth popup and store tokens
  useEffect(() => {
    const handleMessage = async (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return
      if (event.data.type !== 'mcp_oauth_tokens') return

      const { serverId, tokens } = event.data
      const messageOrgServer =
        pendingOAuthOrgServerRef.current ?? activeOrgServer

      if (
        !messageOrgServer ||
        serverId !== messageOrgServer.mcp_server.id ||
        pendingOAuthServerIdRef.current !== serverId
      ) {
        return
      }

      onStoreStart?.()

      try {
        const response = await fetch(
          `${API_URL}/api/v1/mcp-tokens/${serverId}`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${accessTokenRef.current}`,
              'X-Organization-Id': orgId,
            },
            body: JSON.stringify({
              access_token: tokens.access_token,
              refresh_token: tokens.refresh_token,
              token_expiry: tokens.token_expiry,
              client_id: tokens.client_id,
              client_secret: tokens.client_secret,
              provider_metadata: tokens.provider_metadata,
            }),
          },
        )

        if (!response.ok) {
          throw new SweetspotError({
            name: 'FETCH_ERROR',
            message: await readMCPTokenStoreError(response),
            cause: response,
          })
        }

        queryClient.setQueryData<OrganizationMCPServer[]>(
          enabledMcpServersQueryKeys.organization({ orgId }),
          (current) =>
            (current ?? []).map((server) =>
              server.mcp_server_id === messageOrgServer.mcp_server_id
                ? {
                    ...server,
                    user_has_token: true,
                    needs_reauth: false,
                  }
                : server,
            ),
        )

        await queryClient.invalidateQueries({
          queryKey: enabledMcpServersQueryKeys.organization({ orgId }),
        })

        toast.success(
          `Successfully connected to ${messageOrgServer.mcp_server.display_name}.`,
        )
        pendingOAuthServerIdRef.current = null
        pendingOAuthOrgServerRef.current = null
        onTokensStored?.()
      } catch (error) {
        console.error('Failed to store MCP tokens:', error)
        toast.error('Failed to save authentication. Please try again.')
        pendingOAuthServerIdRef.current = null
        pendingOAuthOrgServerRef.current = null
        onStoreError?.()
      }
    }

    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [
    activeOrgServer,
    orgId,
    queryClient,
    onTokensStored,
    onStoreStart,
    onStoreError,
  ])

  const startOAuthFlow = useCallback(
    async (options?: StartOAuthFlowOptions) => {
      const flowOrgServer = options?.orgServer ?? activeOrgServer
      if (!flowOrgServer) {
        toast.error('Integration is not configured for this organization.')
        return
      }

      pendingOAuthServerIdRef.current = flowOrgServer.mcp_server.id
      pendingOAuthOrgServerRef.current = flowOrgServer

      try {
        await authenticateMCPServer(
          flowOrgServer,
          user?.userId,
          orgId || undefined,
          accessToken ?? undefined,
          options
            ? { forceConsentPrompt: options.forceConsentPrompt }
            : undefined,
        )
      } catch (error) {
        pendingOAuthServerIdRef.current = null
        pendingOAuthOrgServerRef.current = null
        throw error
      }
    },
    [activeOrgServer, user?.userId, orgId, accessToken],
  )

  const startAdminConsentFlow = useCallback(
    async (permissionLabels: string[]) => {
      if (!microsoftOrgServer) {
        toast.error(
          'Microsoft integration is not configured for this organization.',
        )
        return
      }

      await requestMicrosoftAdminConsent(microsoftOrgServer, permissionLabels)
      await persistManagedMicrosoftAdminConsent({
        accessToken: accessTokenRef.current,
        microsoftOrgServer,
        orgId,
      })
      await queryClient.invalidateQueries({
        queryKey: enabledMcpServersQueryKeys.organization({ orgId }),
      })
      toast.success('Successfully recorded Microsoft org-wide consent.')
    },
    [microsoftOrgServer, orgId, queryClient],
  )

  return { startOAuthFlow, startAdminConsentFlow }
}
